import { type CallsClient, CallsError } from '@wtfalch/calls';
import type {
  CallEndpoint,
  CallEndpointAdapter,
  CallEndpointStore,
  CallStats,
} from '@wtfalch/calls/browser';
import { createCallEndpoint } from '@wtfalch/calls/browser';
import { useEffect, useRef, useState } from 'react';
import { applyRememberedDevices } from './devicePreference.ts';
import type { CallEndpointRequestOptions } from './roomCalls.ts';

/**
 * A join that never settles -- neither resolving nor rejecting -- must not
 * hang teardown forever: that would leave this mount's mic/camera capture
 * live (nothing ever calls `endpoint.leave()`) and block every later join
 * queued behind `pendingLeaveRef`. Ten seconds is generous for a connect
 * that is actually going to finish; past it, teardown proceeds without it.
 */
const JOIN_TEARDOWN_TIMEOUT_MS = 10_000;

/**
 * The most recent unmount's full teardown, mirrored out of the hook so the
 * call session (`CallSession.tsx`) can wait for it before it seats the same
 * person in a call again -- joining while the last seat is still being given
 * up is what the server answers with a conflict.
 */
let lastTeardown: Promise<void> = Promise.resolve();
export function callTeardownSettled(): Promise<void> {
  return lastTeardown;
}

/** Resets the handoff above between tests (agora#487). A promise one
 *  test's unmount left pending can depend on a timer that test's own
 *  `vi.useRealTimers()` already tore down, so without this a later test in
 *  the same file could await a `lastTeardown` that never settles. */
export function resetCallTeardownForTests(): void {
  lastTeardown = Promise.resolve();
}

function settleWithTimeout(promise: Promise<unknown>, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);
    promise.then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      () => {
        clearTimeout(timer);
        resolve();
      },
    );
  });
}

/**
 * Joins a call session already under way and keeps its live `CallStats` in
 * state -- the one hook the room and calendar entry points both call once a
 * session exists (agora#251, agora#339, agora#340). It never starts a
 * session: `CallsClient.createSession` is upstream of this hook, in
 * whichever entry point calls it.
 *
 * The `CallEndpoint` this returns is what `CallDock`/`CallGrid` call for
 * everything -- `attach`, `setMicrophoneEnabled`, `setMode`, `leave` --
 * never `adapter` directly: the endpoint maps a participant id to the
 * roster's own identity before reaching the adapter (`attach`), and it is
 * the only side that knows a call is actually over (`leave` stops
 * credential renewal too, which `adapter.leave()` alone does not -- see
 * `leaveCall`'s docblock below).
 *
 * The endpoint is recreated only when `adapter` or `store` themselves
 * change identity, never on every render, so the same engine instance
 * lives across the mount this hook's own effect joins and the unmount that
 * leaves it.
 *
 * `error` is whatever `join()` rejected with -- an expired invite, a
 * denied admission, a session already ended -- rather than a silently
 * stuck `stats === null` that looks like a slow connection and never
 * recovers. A caller that mounts this hook is expected to render it.
 */
export function useCallEndpoint({
  adapter,
  store,
  client,
  sessionId,
  mode,
  fetchImpl,
  endpointOptions,
}: {
  adapter: CallEndpointAdapter;
  store: CallEndpointStore;
  /** Told to leave the roster, best effort, on unmount -- see the effect cleanup below. */
  client: CallsClient;
  sessionId: string;
  mode?: 'audio' | 'video';
  fetchImpl?: typeof fetch;
  endpointOptions?: CallEndpointRequestOptions;
}): { endpoint: CallEndpoint; stats: CallStats | null; error: unknown } {
  const createdRef = useRef<{
    adapter: CallEndpointAdapter;
    store: CallEndpointStore;
    endpoint: CallEndpoint;
  } | null>(null);
  if (
    !createdRef.current ||
    createdRef.current.adapter !== adapter ||
    createdRef.current.store !== store
  ) {
    createdRef.current = {
      adapter,
      store,
      endpoint: createCallEndpoint({ adapter, store, fetchImpl, ...endpointOptions }),
    };
  }
  const endpoint = createdRef.current.endpoint;
  const [stats, setStats] = useState<CallStats | null>(null);
  const [error, setError] = useState<unknown>(null);

  // `join()`'s `mode` is a connect-time choice, not a live toggle --
  // `CallGrid`'s camera button calls `endpoint.setMode()` directly for
  // that. Reading it from a ref rather than the effect's dependency list
  // means a parent re-rendering with a different `mode` value never
  // reconnects a call already under way.
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // Holds the full teardown chain from the most recent unmount -- the
  // settled join, then `endpoint.leave()`, then `client.leave()`, in that
  // order (never null once one has happened, and never rejects) -- so a
  // remount's join always waits for all of it rather than racing any part
  // of it. Without this, StrictMode's mount -> cleanup -> mount fired the
  // second join while the first mount's teardown was still running, and a
  // remount's join could complete before a slow `client.leave()` from the
  // previous mount told the server this admission was gone -- dropping the
  // very participant who just rejoined.
  const pendingLeaveRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    let cancelled = false;
    // The in-flight join from *this* effect run only -- a closure-local
    // variable, not a ref. A ref is shared across every mount of this
    // component instance, so the next effect run's own setup (on a
    // `sessionId` change, say) resetting a shared "current join" ref to
    // `null` for itself would clobber the value this mount's cleanup,
    // below, is waiting to read -- even though that `.then` callback runs
    // later, lazily, after the next mount's synchronous body has already
    // reset it. A local variable belongs only to this closure, so this
    // mount's cleanup always sees this mount's own join, however many
    // further mounts have already started by the time it actually settles.
    let myJoin: Promise<void> | null = null;
    const unsubscribe = endpoint.subscribe(setStats);
    setError(null);

    const attemptJoin = () => {
      if (cancelled) return;
      myJoin = endpoint
        .join({ sessionId, mode: modeRef.current })
        .then(() => {
          if (cancelled) return;
          setError(null);
          void applyRememberedDevices(endpoint);
        })
        .catch((joinError: unknown) => {
          if (!cancelled) setError(joinError);
        });
    };

    if (pendingLeaveRef.current) {
      pendingLeaveRef.current.then(attemptJoin);
    } else {
      attemptJoin();
    }

    return () => {
      cancelled = true;
      unsubscribe();
      // Chained off the *previous* teardown, not run immediately: in
      // StrictMode's mount -> cleanup -> mount -> cleanup, a second unmount
      // can fire before the first teardown has settled, and its own leave
      // must not land concurrently with (or ahead of) the first's.
      const previousTeardown = pendingLeaveRef.current ?? Promise.resolve();
      pendingLeaveRef.current = previousTeardown
        .then(() => settleWithTimeout(myJoin ?? Promise.resolve(), JOIN_TEARDOWN_TIMEOUT_MS))
        .then(() => endpoint.leave().catch(() => {}))
        .then(async () => {
          // Best effort, and deliberately not surfaced to the caller: there
          // is nothing left here to roll back or retry once the component
          // is gone, but the server roster should stop listing this
          // participant now rather than wait for the session service's own
          // timeout. Chained after `endpoint.leave()` (not fired alongside
          // it) so a remount's join -- gated on this same promise -- never
          // starts before the server has been told this admission ended.
          // The whole call is inside this `try` (not `.catch()` appended
          // after it) so a *synchronous* throw while building its
          // arguments -- `crypto.randomUUID()` in an insecure context, say
          // -- is caught too: `client.leave(...)` would otherwise never
          // return a promise to attach `.catch()` to, and the throw would
          // reject this chain, skip the next queued join entirely, and
          // surface as an unhandled rejection.
          try {
            await client.leave(sessionId, { requestId: crypto.randomUUID() });
          } catch {
            // Best effort; see above.
          }
        });
      lastTeardown = pendingLeaveRef.current;
    };
  }, [endpoint, sessionId, client]);

  return { endpoint, stats, error };
}

/**
 * Leaving a call is two calls, not one. `endpoint.leave()` is the media
 * side -- it stops this browser's capture, tears down its connection and
 * ends credential renewal for good. `client.leave(sessionId, …)` is the
 * control side: it tells the session service this admission is gone, so
 * `CallsClient.roomStatus`'s participant count and everyone else's roster
 * drop it too.
 *
 * Media teardown always runs first and is always awaited, so a caller's
 * `onLeft` -- fired once this promise resolves -- never fires before this
 * browser's own camera and microphone are actually off. A `client.leave`
 * failure is reported through `onControlLeaveError` rather than thrown or
 * swallowed: by the time it could happen the user has already left
 * locally, so there is nothing left here to roll back, only a roster that
 * may still list them until the service is told. A failure to leave media
 * itself, by contrast, does reject -- there is nothing safe to tell the
 * caller "you've left" about yet.
 */
export async function leaveCall(
  endpoint: CallEndpoint,
  client: CallsClient,
  sessionId: string,
  onControlLeaveError?: (error: unknown) => void,
): Promise<void> {
  await endpoint.leave();
  try {
    await client.leave(sessionId, { requestId: crypto.randomUUID() });
  } catch (error) {
    onControlLeaveError?.(error);
  }
}

/**
 * Renders whatever `useCallEndpoint`'s `join()` rejected with -- an expired
 * invite, a denied admission, a session already ended -- as the short alert
 * text `CallDock`/`CallGrid` show. `capacity_reached` (`CallsError.reason`,
 * the service's own 25-person cap, calls#95) gets the one message worth
 * saying by hand rather than whatever wording the service's own `Error#message`
 * carries; every other case follows this repo's own convention for an
 * unknown rejection (`src/search/model.ts`, `src/files/Files.tsx`): a real
 * `Error`'s own `message`, or `fallback` for anything else.
 */
export function describeCallError(error: unknown, fallback: string): string {
  if (error instanceof CallsError && error.reason === 'capacity_reached') {
    return 'This call is full (25 people).';
  }
  if (error instanceof CallsError && error.code === 'conflict' && error.reason === undefined) {
    return 'You are already on this call, here or in another window. Leave it there first.';
  }
  return error instanceof Error ? error.message : fallback;
}
